import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  listarRecursos,
  subirRecurso,
  getLibraryCategories,
  getLibrarySubcategories,
  createLibrarySubcategory,
} from "../api";
import "./ResourcePage.css";
import NavBar from "./NavBar";

const emptyFilters = {
  search: "",
  age: "",
  area: "",
  specialty: "",
  resource_type: "",
  difficulty: "",
};

const emptyForm = {
  title: "",
  description: "",
  age_min: "",
  age_max: "",
  area: "",
  area_folder_id: "",
  subarea: "",
  subarea_folder_id: "",
  objective: "",
  specialty: "",
  resource_type: "",
  difficulty: "medio",
  notes: "",
};

export default function ResourcePage() {
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("buscar");
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  const [filterCategories, setFilterCategories] = useState([]);
  const [formCategories, setFormCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);

  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingSubcategories, setLoadingSubcategories] = useState(false);
  const [newSubcategoryName, setNewSubcategoryName] = useState("");

  const [filters, setFilters] = useState(emptyFilters);
  const [form, setForm] = useState(emptyForm);
  const [file, setFile] = useState(null);

  const loadResources = async () => {
    setLoading(true);
    setMessage("");

    try {
      const items = await listarRecursos(filters);
      setResources(items);
    } catch (error) {
      setMessage(error.message || "No se pudieron cargar los recursos.");
    } finally {
      setLoading(false);
    }
  };

  const loadFormCategories = async (specialty = form.specialty) => {
    if (!specialty) {
      setFormCategories([]);
      return;
    }

    setLoadingCategories(true);

    try {
      const items = await getLibraryCategories(specialty);
      setFormCategories(items);
    } catch (error) {
      setMessage(error.message || "No se pudieron cargar las áreas.");
    } finally {
      setLoadingCategories(false);
    }
  };

  const loadFilterCategories = async (specialty = filters.specialty) => {
    if (!specialty) {
      setFilterCategories([]);
      return;
    }

    try {
      const items = await getLibraryCategories(specialty);
      setFilterCategories(items);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      loadResources();
    }, 400);

    return () => clearTimeout(timer);
  }, [filters]);

  useEffect(() => {
    if (activeTab === "subir") {
      loadFormCategories(form.specialty);
    }
  }, [activeTab, form.specialty]);

  useEffect(() => {
    if (activeTab === "buscar") {
      loadFilterCategories(filters.specialty);
    }
  }, [activeTab, filters.specialty]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;

    setFilters((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "specialty" ? { area: "" } : {}),
    }));

    if (name === "specialty") {
      setFilterCategories([]);
    }
  };

  const clearFilters = () => {
    setFilters(emptyFilters);
    setFilterCategories([]);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "specialty"
        ? {
            area: "",
            area_folder_id: "",
            subarea: "",
            subarea_folder_id: "",
          }
        : {}),
    }));

    if (name === "specialty") {
      setSubcategories([]);
      setNewSubcategoryName("");
    }
  };

  const handleCategoryChange = async (e) => {
    const folderId = e.target.value;
    const selected = formCategories.find((cat) => cat.id === folderId);

    setForm((prev) => ({
      ...prev,
      area: selected?.name || "",
      area_folder_id: folderId,
      subarea: "",
      subarea_folder_id: "",
    }));

    setSubcategories([]);
    setNewSubcategoryName("");

    if (!folderId) return;

    setLoadingSubcategories(true);

    try {
      const items = await getLibrarySubcategories(folderId);
      setSubcategories(items);
    } catch (error) {
      setMessage(error.message || "No se pudieron cargar las subáreas.");
    } finally {
      setLoadingSubcategories(false);
    }
  };

  const handleSubcategoryChange = (e) => {
    const folderId = e.target.value;
    const selected = subcategories.find((sub) => sub.id === folderId);

    setForm((prev) => ({
      ...prev,
      subarea: selected?.name || "",
      subarea_folder_id: folderId,
    }));
  };

  const handleCreateSubcategory = async () => {
    setMessage("");

    if (!form.area_folder_id) {
      setMessage("Primero selecciona un área.");
      return;
    }

    if (!newSubcategoryName.trim()) {
      setMessage("Escribe el nombre de la nueva subárea.");
      return;
    }

    try {
      const folder = await createLibrarySubcategory(
        form.area_folder_id,
        newSubcategoryName.trim()
      );

      setSubcategories((prev) => [...prev, folder]);

      setForm((prev) => ({
        ...prev,
        subarea: folder.name,
        subarea_folder_id: folder.id,
      }));

      setNewSubcategoryName("");
      setMessage("Subárea creada correctamente.");
    } catch (error) {
      setMessage(error.message || "No se pudo crear la subárea.");
    }
  };

  const resetForm = () => {
    setForm(emptyForm);
    setFile(null);
    setFormCategories([]);
    setSubcategories([]);
    setNewSubcategoryName("");
  };

  const handleUploadResource = async (e) => {
    e.preventDefault();
    setMessage("");

    if (!file) {
      setMessage("Debes seleccionar un archivo.");
      return;
    }

    try {
      const formData = new FormData();

      Object.entries(form).forEach(([key, value]) => {
        formData.append(key, value);
      });

      formData.append("file", file);

      await subirRecurso(formData);

      setMessage("Recurso subido correctamente.");
      resetForm();
      setActiveTab("buscar");
      await loadResources();
    } catch (error) {
      setMessage(error.message || "No se pudo subir el recurso.");
    }
  };

  const fileUrl = (path) => {
    if (!path) return "#";
    if (path.startsWith("http")) return path;
    return `https://te-ayudo.es${path}`;
  };

  const openResource = (resource) => {
    if (resource.content_type === "interactive_game") {
      navigate(`/games/${resource.game_key}`);
      return;
    }

    window.open(resource.drive_url || fileUrl(resource.file_path), "_blank");
  };

  return (
    <>
      <NavBar />

      <div className="resources-page">
        <div className="resources-header">
          <div>
            <h1>Recursos</h1>
            <p>
              Biblioteca clínica interna para buscar, subir y organizar
              materiales.
            </p>
          </div>

          <div className="resources-tabs">
            <button
              type="button"
              className={activeTab === "buscar" ? "active" : ""}
              onClick={() => setActiveTab("buscar")}
            >
              Buscar recursos
            </button>

            <button
              type="button"
              className={activeTab === "subir" ? "active" : ""}
              onClick={() => setActiveTab("subir")}
            >
              Subir recurso
            </button>
          </div>
        </div>

        {message && <div className="resources-message">{message}</div>}

        {activeTab === "buscar" && (
          <>
            <section className="resources-filters">
              <input
                name="search"
                placeholder="Buscar por nombre, descripción u objetivo..."
                value={filters.search}
                onChange={handleFilterChange}
              />

              <select
                name="specialty"
                value={filters.specialty}
                onChange={handleFilterChange}
              >
                <option value="">Especialidad</option>
                <option value="psicologia">Psicología</option>
                <option value="logopedia">Logopedia</option>
                <option value="psicopedagogia">Psicopedagogía</option>
                <option value="nutricion">Nutrición</option>
              </select>

              <select
                name="area"
                value={filters.area}
                onChange={handleFilterChange}
                disabled={!filters.specialty}
              >
                <option value="">
                  {!filters.specialty ? "Área" : "Todas las áreas"}
                </option>

                {filterCategories.map((category) => (
                  <option key={category.id} value={category.name}>
                    {category.name}
                  </option>
                ))}
              </select>

              <input
                name="age"
                type="number"
                placeholder="Edad"
                value={filters.age}
                onChange={handleFilterChange}
              />

              <select
                name="resource_type"
                value={filters.resource_type}
                onChange={handleFilterChange}
              >
                <option value="">Tipo</option>
                <option value="ficha">Ficha</option>
                <option value="juego">Juego</option>
                <option value="cuento">Cuento</option>
                <option value="registro">Registro</option>
                <option value="pauta_padres">Pauta para padres</option>
                <option value="protocolo">Protocolo</option>
                <option value="prueba">Prueba</option>
              </select>

              <select
                name="difficulty"
                value={filters.difficulty}
                onChange={handleFilterChange}
              >
                <option value="">Dificultad</option>
                <option value="facil">Fácil</option>
                <option value="medio">Medio</option>
                <option value="dificil">Difícil</option>
              </select>

              <button type="button" onClick={loadResources}>
                Filtrar
              </button>

              <button
                type="button"
                className="clear-filters-btn"
                onClick={clearFilters}
              >
                Limpiar
              </button>
            </section>

            <section className="resources-list">
              {loading ? (
                <p>Cargando recursos...</p>
              ) : resources.length === 0 ? (
                <p>No hay recursos con esos filtros.</p>
              ) : (
                resources.map((resource) => (
                  <article key={resource.id} className="resource-card">
                    <div className="resource-card-header">
                      <h3>{resource.title}</h3>
                      <span>{resource.difficulty || "medio"}</span>
                    </div>

                    <p>{resource.description || "Sin descripción."}</p>

                    <div className="resource-tags">
                      {resource.area && <small>{resource.area}</small>}
                      {resource.subarea && <small>{resource.subarea}</small>}
                      {resource.specialty && (
                        <small>{resource.specialty}</small>
                      )}
                      {resource.resource_type && (
                        <small>{resource.resource_type}</small>
                      )}
                      {resource.content_type === "interactive_game" && (
                        <small>Juego interactivo</small>
                      )}

                      {(resource.age_min || resource.age_max) && (
                        <small>
                          {resource.age_min || "?"}-{resource.age_max || "?"}{" "}
                          años
                        </small>
                      )}
                    </div>

                    {resource.objective && (
                      <p className="resource-objective">
                        <strong>Objetivo:</strong> {resource.objective}
                      </p>
                    )}

                    <div className="resource-actions">
                      {resource.content_type === "interactive_game" ? (
                        <button
                          type="button"
                          className="play-button"
                          onClick={() => openResource(resource)}
                        >
                          <i className="fas fa-play"></i>
                          <span>Jugar</span>
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="open-button"
                          onClick={() => openResource(resource)}
                        >
                          <i className="fas fa-folder-open"></i>
                          <span>Abrir</span>
                        </button>
                      )}
                    </div>
                  </article>
                ))
              )}
            </section>
          </>
        )}

        {activeTab === "subir" && (
          <form
            className="upload-resource-form"
            onSubmit={handleUploadResource}
          >
            <div className="form-grid">
              <input
                name="title"
                placeholder="Nombre del recurso"
                value={form.title}
                onChange={handleFormChange}
                required
              />

              <select
                name="specialty"
                value={form.specialty}
                onChange={handleFormChange}
                required
              >
                <option value="">Especialidad</option>
                <option value="psicologia">Psicología</option>
                <option value="logopedia">Logopedia</option>
                <option value="psicopedagogia">Psicopedagogía</option>
                <option value="nutricion">Nutrición</option>
              </select>

              <input
                name="age_min"
                type="number"
                placeholder="Edad mínima"
                value={form.age_min}
                onChange={handleFormChange}
              />

              <input
                name="age_max"
                type="number"
                placeholder="Edad máxima"
                value={form.age_max}
                onChange={handleFormChange}
              />

              <select
                name="area_folder_id"
                value={form.area_folder_id}
                onChange={handleCategoryChange}
                required
                disabled={!form.specialty}
              >
                <option value="">
                  {!form.specialty
                    ? "Primero elige especialidad"
                    : loadingCategories
                    ? "Cargando áreas..."
                    : "Área"}
                </option>

                {formCategories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>

              <select
                name="subarea_folder_id"
                value={form.subarea_folder_id}
                onChange={handleSubcategoryChange}
                disabled={!form.area_folder_id}
              >
                <option value="">
                  {loadingSubcategories ? "Cargando subáreas..." : "Subárea"}
                </option>

                {subcategories.map((subcategory) => (
                  <option key={subcategory.id} value={subcategory.id}>
                    {subcategory.name}
                  </option>
                ))}
              </select>

              <input
                name="objective"
                placeholder="Objetivo específico"
                value={form.objective}
                onChange={handleFormChange}
              />

              <select
                name="resource_type"
                value={form.resource_type}
                onChange={handleFormChange}
              >
                <option value="">Tipo de recurso</option>
                <option value="ficha">Ficha</option>
                <option value="juego">Juego</option>
                <option value="cuento">Cuento</option>
                <option value="registro">Registro</option>
                <option value="pauta_padres">Pauta para padres</option>
                <option value="protocolo">Protocolo</option>
                <option value="prueba">Prueba</option>
              </select>

              <select
                name="difficulty"
                value={form.difficulty}
                onChange={handleFormChange}
              >
                <option value="facil">Fácil</option>
                <option value="medio">Medio</option>
                <option value="dificil">Difícil</option>
              </select>
            </div>

            <div className="new-subcategory-row">
              <input
                type="text"
                placeholder="Crear nueva subárea"
                value={newSubcategoryName}
                onChange={(e) => setNewSubcategoryName(e.target.value)}
                disabled={!form.area_folder_id}
              />

              <button
                type="button"
                onClick={handleCreateSubcategory}
                disabled={!form.area_folder_id}
              >
                Crear
              </button>
            </div>

            <textarea
              name="description"
              placeholder="Descripción breve"
              value={form.description}
              onChange={handleFormChange}
            />

            <textarea
              name="notes"
              placeholder="Observaciones de uso"
              value={form.notes}
              onChange={handleFormChange}
            />

            <input
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />

            <button type="submit">Subir recurso</button>
          </form>
        )}
      </div>
    </>
  );
}